import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api, apiBlob } from "../lib/api.ts";
import { useApi, formatDate, relativeTime, titleCase } from "../lib/hooks.ts";
import { useAuth } from "../lib/auth.tsx";
import type {
  DeviceDetail, RequestType, ProcessesResponse,
  ProgramsResponse, UpdatesResponse, BlocklistResponse, BackupsResponse,
  LicensesResponse, HealthResponse, BiosDump, BiosDumpsResponse,
} from "../lib/types.ts";
import { Badge, Card, Empty, ErrorAlert, Modal, Spinner } from "../components/ui.tsx";

interface ActionDef {
  type: RequestType;
  label: string;
  danger?: boolean;
  description: string;
}

const ACTIONS: ActionDef[] = [
  { type: "freeze", label: "Freeze", description: "Bloqueia o uso do dispositivo ate liberacao remota." },
  { type: "wipe", label: "Wipe", danger: true, description: "Apaga dados do endpoint (acao irreversivel)." },
  { type: "file-delete", label: "Excluir arquivo", danger: true, description: "Remove um arquivo especifico pela rota informada." },
  { type: "message", label: "Enviar mensagem", description: "Exibe aviso na tela do usuario final." },
  { type: "script", label: "Executar script", description: "Roda um script da biblioteca Reach no endpoint." },
  { type: "inventory-programs", label: "Inventario de programas", description: "Coleta lista de programas instalados." },
  { type: "inventory-updates", label: "Verificar atualizacoes", description: "Consulta atualizacoes pendentes do Windows." },
  { type: "install-updates", label: "Instalar atualizacoes", description: "Instala atualizacoes pendentes." },
  { type: "inventory-processes", label: "Processos ativos", description: "Lista processos em execucao." },
  { type: "backup", label: "Backup de seguranca", description: "Copia documentos e registro." },
  { type: "bios_backup", label: "Backup de BIOS", description: "Le a ROM da placa-mae via driver (somente leitura) e envia ao console." },
];

function licenseBadge(status: string): { value: string; label: string } {
  switch (status) {
    case "ativado":
      return { value: "completed", label: "Ativado" };
    case "instalado":
      return { value: "info", label: "Instalado" };
    case "digital":
      return { value: "info", label: "Licenca digital" };
    case "nao-licenciado":
    case "nao-genuino":
      return { value: "critical", label: titleCase(status) };
    case "notificacao":
      return { value: "warning", label: "Notificacao" };
    default:
      if (status.startsWith("graca")) return { value: "warning", label: titleCase(status) };
      return { value: "unknown", label: status || "—" };
  }
}

export function DeviceDetailPage() {
  const { uid = "" } = useParams();
  const { can } = useAuth();
  const { data, error, loading, reload } = useApi<DeviceDetail>(
    uid ? `/api/v3/devices/${uid}` : null,
    [],
    { poll: 5000 },
  );
  const [active, setActive] = useState<ActionDef | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [freezeHours, setFreezeHours] = useState("4");
  const [freezeMessage, setFreezeMessage] = useState("Contate o suporte para liberacao do dispositivo.");
  const [wipeScope, setWipeScope] = useState("os");
  const [wipeConfirm, setWipeConfirm] = useState(false);
  const [filePath, setFilePath] = useState("C:/Users/Public/documento.docx");
  const [messageBody, setMessageBody] = useState("Seu dispositivo passara por manutencao hoje.");
  const [scriptName, setScriptName] = useState("coletar-inventario");
  const [blockPattern, setBlockPattern] = useState("");
  const [blockAction, setBlockAction] = useState<"block" | "unblock">("block");
  const [backupType, setBackupType] = useState("quick");

  // Estados das novas secoes
  const [showPrograms, setShowPrograms] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);
  const [showBlocklist, setShowBlocklist] = useState(false);
  const [showBackups, setShowBackups] = useState(false);
  const [showBios, setShowBios] = useState(false);
  const [biosBusy, setBiosBusy] = useState(false);
  const [allowTestMode, setAllowTestMode] = useState(false);

  const { data: programs, loading: progLoading, reload: reloadPrograms } = useApi<ProgramsResponse>(
    showPrograms && uid ? `/api/v3/devices/${uid}/programs` : null,
  );
  const { data: updates, loading: updLoading, reload: reloadUpdates } = useApi<UpdatesResponse>(
    showUpdates && uid ? `/api/v3/devices/${uid}/updates` : null,
  );
  const { data: blocklist, loading: blLoading, reload: reloadBlocklist } = useApi<BlocklistResponse>(
    showBlocklist && uid ? `/api/v3/devices/${uid}/blocklist` : null,
  );
  const { data: backups, loading: bkLoading, reload: reloadBackups } = useApi<BackupsResponse>(
    showBackups && uid ? `/api/v3/devices/${uid}/backups` : null,
  );
  const { data: biosDumps, loading: biosLoading, reload: reloadBios } = useApi<BiosDumpsResponse>(
    showBios && uid ? `/api/v3/devices/${uid}/bios-dumps` : null,
  );

  async function downloadBiosDump(d: BiosDump) {
    try {
      const blob = await apiBlob(`/api/v3/bios-dumps/${d.uid}/download`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      setSuccess(err instanceof ApiError ? err.message : "Falha ao baixar dump");
    }
  }

  // Processos em execucao — base do dropdown de bloqueio (o agente casa o
  // padrao contra o nome do processo, ex.: "chrome"). Busca na montagem e
  // repete ate 3x enquanto vazio (o endpoint cria o comando de coleta e o
  // agente responde no proximo checkin, ~10-20s).
  const [procs, setProcs] = useState<ProcessesResponse["processes"]>([]);
  const [procsLoading, setProcsLoading] = useState(false);
  const [blockSource, setBlockSource] = useState<"proc" | "manual">("proc");

  const loadProcesses = useCallback(async () => {
    setProcsLoading(true);
    try {
      const res = await api<ProcessesResponse>(`/api/v3/devices/${uid}/processes`);
      setProcs(res.processes ?? []);
    } catch {
      setProcs([]);
    } finally {
      setProcsLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    let tries = 0;
    let timer = 0;
    const tick = async () => {
      try {
        const res = await api<ProcessesResponse>(`/api/v3/devices/${uid}/processes`);
        if (cancelled) return;
        setProcs(res.processes ?? []);
        if ((res.processes?.length ?? 0) === 0 && tries < 3) {
          tries += 1;
          timer = window.setTimeout(tick, 8000);
        }
      } catch {
        /* proxima tentativa do botao */
      }
    };
    timer = window.setTimeout(tick, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [uid]);

  const blockOptions = useMemo(() => {
    const byName = new Map<string, { value: string; count: number; mem: number }>();
    for (const p of procs) {
      if (!p?.name) continue;
      const key = p.name.toLowerCase();
      const cur = byName.get(key);
      if (cur) {
        cur.count += 1;
        cur.mem += p.memoryMb ?? 0;
      } else {
        byName.set(key, { value: p.name, count: 1, mem: p.memoryMb ?? 0 });
      }
    }
    const blocked = new Set((blocklist?.blocklist ?? []).map((b) => b.pattern));
    return Array.from(byName.values())
      .sort((a, b) => a.value.localeCompare(b.value))
      .map((o) => {
        const isBlocked = blocked.has(o.value);
        const extras = [
          `${o.count}x`,
          o.mem > 0 ? `${Math.round(o.mem)} MB` : null,
          isBlocked ? "ja bloqueado" : null,
        ]
          .filter(Boolean)
          .join(" · ");
        return { value: o.value, label: `${o.value} · ${extras}`, blocked: isBlocked };
      });
  }, [procs, blocklist]);

  // Ao mudar para "Desbloquear", carrega a blocklist atual (fonte do dropdown)
  useEffect(() => {
    if (blockAction === "unblock") {
      setShowBlocklist(true);
      reloadBlocklist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockAction]);

  // ── Licencas de software ──
  const [licenses, setLicenses] = useState<LicensesResponse | null>(null);
  const [licLoading, setLicLoading] = useState(false);
  const [showLicenses, setShowLicenses] = useState(false);

  const loadLicenses = useCallback(async () => {
    setLicLoading(true);
    setShowLicenses(true);
    try {
      let res = await api<LicensesResponse>(`/api/v3/devices/${uid}/licenses`);
      setLicenses(res);
      // comando recem-criado: aguarda o agente responder e tenta de novo
      for (let i = 0; i < 3 && res.commandUid && (res.count ?? 0) === 0; i++) {
        await new Promise((r) => window.setTimeout(r, 8000));
        res = await api<LicensesResponse>(`/api/v3/devices/${uid}/licenses`);
        setLicenses(res);
      }
    } catch {
      /* proxima tentativa pelo botao */
    } finally {
      setLicLoading(false);
    }
  }, [uid]);

  // ── Saude do hardware ──
  const [healthRes, setHealthRes] = useState<HealthResponse | null>(null);
  const [hltLoading, setHltLoading] = useState(false);
  const [showHealth, setShowHealth] = useState(false);

  const loadHealth = useCallback(async () => {
    setHltLoading(true);
    setShowHealth(true);
    try {
      let res = await api<HealthResponse>(`/api/v3/devices/${uid}/health`);
      setHealthRes(res);
      for (let i = 0; i < 3 && res.commandUid && !res.health; i++) {
        await new Promise((r) => window.setTimeout(r, 8000));
        res = await api<HealthResponse>(`/api/v3/devices/${uid}/health`);
        setHealthRes(res);
      }
    } catch {
      /* proxima tentativa pelo botao */
    } finally {
      setHltLoading(false);
    }
  }, [uid]);

  if (loading && !data) return <div className="page"><Spinner /></div>;
  if (error) return <div className="page"><ErrorAlert message={error.message} /></div>;
  if (!data) return null;

  const device = data.device;

  function payloadFor(type: RequestType): Record<string, unknown> {
    switch (type) {
      case "freeze":
        return { duration: Number(freezeHours), message: freezeMessage };
      case "wipe":
        return { scope: wipeScope, confirm: wipeConfirm };
      case "file-delete":
        return { path: filePath };
      case "message":
        return { body: messageBody };
      case "script":
        return { script: scriptName, timeout: 300 };
      case "block-program":
        return { pattern: blockPattern, action: blockAction };
      case "backup":
        return { backupType };
      default:
        return {};
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!active) return;
    if (active.type === "wipe" && !wipeConfirm) {
      setFormError("Confirme a acao de wipe antes de enviar.");
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      await api(`/api/v3/devices/${uid}/requests`, {
        method: "POST",
        body: { type: active.type, payload: payloadFor(active.type) },
      });
      setSuccess(`Requisicao ${titleCase(active.type)} criada — acompanhe na fila de requisicoes.`);
      setActive(null);
      reload();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Falha ao criar requisicao");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="cell-sub">
            <Link to="/devices">Dispositivos</Link> / {device.uid}
          </p>
          <h1>{device.name}</h1>
          <p className="subtle">
            {device.make} {device.model} · {device.osVersion} · {device.group?.name ?? "sem grupo"}
          </p>
        </div>
        <span className="spacer" />
        <Badge value={device.status} />
        <Badge value={device.compliance} />
        <Badge value={device.encryption} />
      </div>

      {success ? <div className="alert info">{success}</div> : null}

      <div className="grid cols-2">
        <Card title="Identificacao">
          <dl className="kv">
            <dt>UID</dt>
            <dd className="mono">{device.uid}</dd>
            <dt>Serie</dt>
            <dd className="mono">{device.serial}</dd>
            <dt>Usuario</dt>
            <dd>{device.user.name}</dd>
            <dt>Departamento</dt>
            <dd>{device.user.department}</dd>
            <dt>Grupo</dt>
            <dd>{device.group?.name ?? "—"}</dd>
            <dt>Risco</dt>
            <dd style={{ color: device.riskScore >= 70 ? "var(--danger)" : undefined }}>
              {device.riskScore}/100
            </dd>
          </dl>
        </Card>

        <Card title="Hardware e agente">
          <dl className="kv">
            <dt>Plataforma</dt>
            <dd>{device.platform}</dd>
            <dt>Sistema</dt>
            <dd>{device.osVersion}</dd>
            <dt>Agente</dt>
            <dd className="mono">{device.agentVersion}</dd>
            <dt>Ultimo contato</dt>
            <dd>{formatDate(device.lastSeen)}</dd>
            <dt>Criptografia</dt>
            <dd>{titleCase(device.encryption)}</dd>
            <dt>Conformidade</dt>
            <dd>{titleCase(device.compliance)}</dd>
          </dl>
        </Card>
      </div>

      <Card title="Acoes remotas">
        {can("actions.issue") ? (
          <div className="toolbar">
            {ACTIONS.map((action) => (
              <button
                key={action.type}
                className={`btn ${action.danger ? "danger" : "primary"}`}
                onClick={() => {
                  setActive(action);
                  setFormError(null);
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="alert info">
            Seu papel nao possui a permissao <code>actions.issue</code> — leitura apenas.
          </div>
        )}
        <div className="grid cols-2" style={{ marginTop: 16 }}>
          {ACTIONS.map((a) => (
            <div key={a.type}>
              <strong>{a.label}</strong> — <span className="subtle">{a.description}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* ── Programas Instalados ── */}
      <Card title="Programas instalados">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <button
            className="btn primary"
            onClick={() => {
              setShowPrograms(true);
              reloadPrograms();
            }}
          >
            {showPrograms ? "Atualizar lista" : "Ver programas instalados"}
          </button>
          {can("actions.issue") && (
            <button
              className="btn"
              onClick={async () => {
                setBusy(true);
                try {
                  await api(`/api/v3/devices/${uid}/requests`, {
                    method: "POST",
                    body: { type: "inventory-programs" },
                  });
                  setSuccess("Coleta de programas iniciada — recarregue em instantes.");
                } catch {
                  setSuccess(null);
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
            >
              Coletar agora
            </button>
          )}
        </div>
        {showPrograms && (
          progLoading ? <Spinner /> :
          programs && programs.count > 0 ? (
            <div className="table-wrap" style={{ maxHeight: 400, overflow: "auto" }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Versao</th>
                    <th>Fornecedor</th>
                    <th>Instalado</th>
                    <th>Tamanho</th>
                  </tr>
                </thead>
                <tbody>
                  {programs.programs.map((p, i) => (
                    <tr key={i}>
                      <td><div className="cell-title">{p.name}</div></td>
                      <td className="mono">{p.version || "—"}</td>
                      <td>{p.publisher || "—"}</td>
                      <td className="cell-sub">{p.installed || "—"}</td>
                      <td>{p.sizeMb ? `${p.sizeMb} MB` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty>Nenhum programa coletado ainda.</Empty>
        )}
      </Card>

      {/* ── Windows Updates ── */}
      <Card title="Windows Updates">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <button
            className="btn primary"
            onClick={() => {
              setShowUpdates(true);
              reloadUpdates();
            }}
          >
            {showUpdates ? "Atualizar" : "Ver atualizacoes pendentes"}
          </button>
          {can("actions.issue") && (
            <button
              className="btn"
              onClick={async () => {
                setBusy(true);
                try {
                  await api(`/api/v3/devices/${uid}/requests`, {
                    method: "POST",
                    body: { type: "inventory-updates" },
                  });
                  setSuccess("Verificacao de atualizacoes iniciada.");
                } catch { setSuccess(null); } finally { setBusy(false); }
              }}
              disabled={busy}
            >
              Verificar agora
            </button>
          )}
        </div>
        {showUpdates && (
          updLoading ? <Spinner /> :
          updates ? (
            <>
              <p className="subtle">Ultima verificacao: {formatDate(updates.lastCheck)}</p>
              {updates.count === 0 ? (
                <Empty>Nenhuma atualizacao pendente.</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Atualizacao</th>
                        <th>Severidade</th>
                        <th>Tamanho</th>
                      </tr>
                    </thead>
                    <tbody>
                      {updates.updates.map((u, i) => (
                        <tr key={i}>
                          <td><div className="cell-title">{u.title}</div></td>
                          <td><Badge value={u.severity} /></td>
                          <td>{u.sizeMb ? `${u.sizeMb} MB` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : <Empty>Nenhuma verificacao realizada.</Empty>
        )}
      </Card>

      {/* ── Licencas de software ── */}
      <Card title="Licencas de software">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <button className="btn primary" onClick={loadLicenses} disabled={licLoading}>
            {licLoading ? "Coletando..." : showLicenses ? "Atualizar" : "Ver licencas"}
          </button>
          {licenses?.collectedAt ? (
            <span className="muted" style={{ marginLeft: "auto" }}>
              Coletado em {formatDate(licenses.collectedAt)}
            </span>
          ) : null}
        </div>
        {showLicenses ? (
          licLoading && !licenses ? (
            <Spinner />
          ) : licenses && licenses.licenses.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Tipo</th>
                    <th>Canal</th>
                    <th>Chave</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.licenses.map((l, i) => {
                    const b = licenseBadge(l.status);
                    return (
                      <tr key={i}>
                        <td><div className="cell-title">{l.product}</div></td>
                        <td>{titleCase(l.kind)}</td>
                        <td className="cell-sub">{l.channel || "—"}</td>
                        <td className="mono cell-sub">{l.productKey || l.partialKey || "—"}</td>
                        <td><Badge value={b.value} label={b.label} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty>Nenhuma licenca coletada ainda — o agente responde no proximo checkin (~10-20s). Clique em "Atualizar".</Empty>
          )
        ) : null}
      </Card>

      {/* ── Saude do hardware ── */}
      <Card title="Saude do hardware">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <button className="btn primary" onClick={loadHealth} disabled={hltLoading}>
            {hltLoading ? "Coletando..." : showHealth ? "Atualizar" : "Ver saude do hardware"}
          </button>
          {healthRes?.collectedAt ? (
            <span className="muted" style={{ marginLeft: "auto" }}>
              Coletado em {formatDate(healthRes.collectedAt)}
            </span>
          ) : null}
        </div>
        {showHealth ? (
          hltLoading && !healthRes ? (
            <Spinner />
          ) : healthRes?.health ? (
            <>
              {healthRes.health.issues.length > 0 ? (
                <div style={{ marginBottom: 12 }}>
                  {healthRes.health.issues.map((issue, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                      <Badge value="warning" label="Atencao" />
                      <span>{issue}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="subtle" style={{ marginBottom: 12 }}>Nenhum problema detectado.</p>
              )}

              {healthRes.health.disks.length > 0 ? (
                <div className="table-wrap" style={{ marginBottom: 12 }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Disco</th>
                        <th>Midia</th>
                        <th>Tamanho</th>
                        <th>Saude</th>
                        <th>Desgaste</th>
                        <th>Temp</th>
                        <th>Erros R/W</th>
                        <th>Horas ligado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {healthRes.health.disks.map((d, i) => (
                        <tr key={i}>
                          <td>
                            <div className="cell-title">{d.model || "Disco"}</div>
                            <div className="cell-sub mono">{d.serial || "—"}</div>
                          </td>
                          <td className="cell-sub">{[d.media, d.bus].filter(Boolean).join(" · ") || "—"}</td>
                          <td className="cell-sub">{d.sizeGb} GB</td>
                          <td>
                            <Badge
                              value={d.health === "healthy" ? "completed" : "critical"}
                              label={d.health === "healthy" ? "Saudavel" : d.health}
                            />
                          </td>
                          <td className="cell-sub">
                            {d.wear != null ? `${d.wear}%` : "—"}
                          </td>
                          <td className="cell-sub">{d.temperature != null ? `${d.temperature} C` : "—"}</td>
                          <td className="cell-sub">{d.readErrors ?? 0} / {d.writeErrors ?? 0}</td>
                          <td className="cell-sub">{d.powerOnHours != null ? `${d.powerOnHours} h` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              <div className="grid cols-2" style={{ alignItems: "start" }}>
                <section className="panel">
                  <div className="panel-title">Memoria</div>
                  <dl className="kv">
                    <dt>Total</dt>
                    <dd>{healthRes.health.memory ? `${healthRes.health.memory.totalGb} GB` : "—"}</dd>
                    <dt>Slots</dt>
                    <dd>
                      {healthRes.health.memory
                        ? healthRes.health.memory.slotsTotal
                          ? `${healthRes.health.memory.modules}/${healthRes.health.memory.slotsTotal} preenchidos`
                          : `${healthRes.health.memory.modules} modulo(s)`
                        : "—"}
                    </dd>
                    <dt>Velocidade</dt>
                    <dd>{healthRes.health.memory?.speedMax ? `${healthRes.health.memory.speedMax} MHz` : "—"}</dd>
                    <dt>Erros WHEA (30d)</dt>
                    <dd>
                      {healthRes.health.memory ? (
                        <Badge
                          value={healthRes.health.memory.wheaEvents30d > 0 ? "warning" : "completed"}
                          label={healthRes.health.memory.wheaEvents30d > 0 ? `${healthRes.health.memory.wheaEvents30d} evento(s)` : "Nenhum"}
                        />
                      ) : "—"}
                    </dd>
                    {healthRes.health.memory?.lastDiag ? (
                      <>
                        <dt>Diagnostico</dt>
                        <dd className="cell-sub">{healthRes.health.memory.lastDiag}</dd>
                      </>
                    ) : null}
                  </dl>
                </section>

                <section className="panel">
                  <div className="panel-title">Sistema</div>
                  <dl className="kv">
                    <dt>Placa-mae</dt>
                    <dd>{healthRes.health.system?.motherboard || "—"}</dd>
                    <dt>BIOS</dt>
                    <dd>
                      {healthRes.health.system?.bios
                        ? `${healthRes.health.system.bios}${healthRes.health.system.biosDate ? ` (${healthRes.health.system.biosDate})` : ""}`
                        : "—"}
                    </dd>
                    <dt>Tempo ligado</dt>
                    <dd>
                      {healthRes.health.system?.uptimeHours != null
                        ? healthRes.health.system.uptimeHours >= 24
                          ? `${Math.floor(healthRes.health.system.uptimeHours / 24)} d ${healthRes.health.system.uptimeHours % 24} h`
                          : `${healthRes.health.system.uptimeHours} h`
                        : "—"}
                    </dd>
                  </dl>
                </section>

                <section className="panel">
                  <div className="panel-title">Bateria</div>
                  <dl className="kv">
                    <dt>Carga</dt>
                    <dd>
                      {healthRes.health.battery
                        ? healthRes.health.battery.charge != null
                          ? `${healthRes.health.battery.charge}%`
                          : "—"
                        : "Sem bateria (desktop)"}
                    </dd>
                    {healthRes.health.battery?.healthPct != null ? (
                      <>
                        <dt>Saude</dt>
                        <dd>
                          <Badge
                            value={healthRes.health.battery.healthPct < 60 ? "warning" : "completed"}
                            label={`${healthRes.health.battery.healthPct}%`}
                          />
                        </dd>
                      </>
                    ) : null}
                  </dl>
                </section>

                <section className="panel">
                  <div className="panel-title">CPU</div>
                  <dl className="kv">
                    <dt>Processador</dt>
                    <dd>{healthRes.health.cpu?.name || "—"}</dd>
                    <dt>Clock maximo</dt>
                    <dd>{healthRes.health.cpu?.maxClockGhz != null ? `${healthRes.health.cpu.maxClockGhz} GHz` : "—"}</dd>
                    <dt>Temperatura</dt>
                    <dd>{healthRes.health.cpu?.tempC != null ? `${healthRes.health.cpu.tempC} C` : "—"}</dd>
                    <dt>Carga</dt>
                    <dd>{healthRes.health.cpu?.loadPct != null ? `${healthRes.health.cpu.loadPct}%` : "—"}</dd>
                    <dt>Nucleos / threads</dt>
                    <dd>
                      {healthRes.health.cpu?.cores != null
                        ? `${healthRes.health.cpu.cores}C/${healthRes.health.cpu.threads ?? "?"}T`
                        : "—"}
                    </dd>
                  </dl>
                </section>
              </div>
            </>
          ) : (
            <Empty>Sem dados de saude — o agente responde no proximo checkin (~10-20s). Clique em "Atualizar".</Empty>
          )
        ) : null}
      </Card>

      {/* ── Bloqueio de Programas ── */}
      <Card title="Bloqueio de programas">
        {can("actions.issue") ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!blockPattern) return;
              setBusy(true);
              setFormError(null);
              try {
                await api(`/api/v3/devices/${uid}/blocklist`, {
                  method: "POST",
                  body: { pattern: blockPattern, action: blockAction },
                });
                setSuccess(`Programa '${blockPattern}' ${blockAction === "block" ? "bloqueado" : "desbloqueado"}.`);
                setBlockPattern("");
                reloadBlocklist();
              } catch (err) {
                setFormError(err instanceof ApiError ? err.message : "Falha");
              } finally {
                setBusy(false);
              }
            }}
          >
            <div className="grid cols-3" style={{ gap: 12 }}>
              <div className="field">
                <label htmlFor="block-program-select">Programa</label>
                <select
                  id="block-program-select"
                  className="select"
                  value={blockSource === "manual" ? "__manual__" : blockPattern}
                  onChange={(e) => {
                    if (e.target.value === "__manual__") {
                      setBlockSource("manual");
                      setBlockPattern("");
                    } else {
                      setBlockSource("proc");
                      setBlockPattern(e.target.value);
                    }
                  }}
                >
                  <option value="" disabled>
                    {blockAction === "unblock"
                      ? (blocklist?.blocklist?.length ?? 0) > 0
                        ? "Selecione o bloqueio a remover..."
                        : "Nenhum bloqueio ativo"
                      : blockOptions.length > 0
                        ? "Selecione um programa..."
                        : procsLoading
                          ? "Coletando processos..."
                          : "Processos ainda nao coletados"}
                  </option>
                  {blockAction === "unblock" ? (
                    (blocklist?.blocklist ?? []).length > 0 ? (
                      <optgroup label={`Bloqueios ativos (${blocklist?.blocklist?.length ?? 0})`}>
                        {(blocklist?.blocklist ?? []).map((b) => (
                          <option key={b.pattern} value={b.pattern}>
                            {b.pattern} · {formatDate(b.added)}
                          </option>
                        ))}
                      </optgroup>
                    ) : null
                  ) : blockOptions.length > 0 ? (
                    <optgroup label={`Processos em execucao (${blockOptions.length})`}>
                      {blockOptions.map((o) => (
                        <option key={o.value} value={o.value} disabled={o.blocked}>
                          {o.label}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                  <option value="__manual__">Outro (digitar padrao)</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="block-action">Acao</label>
                <select
                  id="block-action"
                  className="select"
                  value={blockAction}
                  onChange={(e) => {
                    setBlockAction(e.target.value as "block" | "unblock");
                    setBlockPattern("");
                    setBlockSource("proc");
                  }}
                >
                  <option value="block">Bloquear</option>
                  <option value="unblock">Desbloquear</option>
                </select>
              </div>
              <div className="field" style={{ display: "flex", alignItems: "flex-end" }}>
                <button className="btn primary" type="submit" disabled={busy || !blockPattern}>
                  {busy ? "Enviando..." : "Aplicar"}
                </button>
              </div>
            </div>
            {blockSource === "manual" ? (
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor="block-pattern">Padrao do processo (regex, sem .exe)</label>
                <input
                  id="block-pattern"
                  className="input"
                  placeholder="ex: chrome"
                  value={blockPattern}
                  onChange={(e) => setBlockPattern(e.target.value)}
                />
              </div>
            ) : null}
            {blockAction === "block" && blockOptions.length === 0 && !procsLoading ? (
              <p className="subtle" style={{ marginTop: 8 }}>
                Nenhum processo coletado ainda — o agente responde no proximo checkin (~10-20s).
                Clique em "Atualizar processos" apos esse periodo, ou use "Outro (digitar padrao)".
              </p>
            ) : null}
            {formError ? <ErrorAlert message={formError} /> : null}
          </form>
        ) : (
          <div className="alert info">Seu papel nao permite alterar o bloqueio.</div>
        )}
        <div className="toolbar" style={{ marginTop: 12 }}>
          {can("actions.issue") ? (
            <button className="btn" type="button" onClick={loadProcesses} disabled={procsLoading}>
              {procsLoading ? "Coletando..." : "Atualizar processos"}
            </button>
          ) : null}
          <button
            className="btn"
            onClick={() => {
              setShowBlocklist(true);
              reloadBlocklist();
            }}
          >
            {showBlocklist ? "Atualizar lista" : "Ver bloqueios atuais"}
          </button>
        </div>
        {showBlocklist && (
          blLoading ? <Spinner /> :
          blocklist && blocklist.blocklist.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Padrao</th><th>Adicionado</th><th>Por</th></tr>
                </thead>
                <tbody>
                  {blocklist.blocklist.map((b, i) => (
                    <tr key={i}>
                      <td className="mono">{b.pattern}</td>
                      <td className="cell-sub">{formatDate(b.added)}</td>
                      <td>{b.addedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty>Nenhum programa bloqueado.</Empty>
        )}
      </Card>

      {/* ── Backups de Seguranca ── */}
      <Card title="Backups de seguranca">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          {can("actions.issue") && (
            <>
              <select className="select" value={backupType} onChange={(e) => setBackupType(e.target.value)} style={{ maxWidth: 160 }}>
                <option value="quick">Backup rapido</option>
                <option value="full">Backup completo</option>
              </select>
              <button
                className="btn primary"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api(`/api/v3/devices/${uid}/backup`, {
                      method: "POST",
                      body: { backupType },
                    });
                    setSuccess("Backup iniciado — acompanhe na lista.");
                    setTimeout(() => reloadBackups(), 2000);
                  } catch { setSuccess(null); } finally { setBusy(false); }
                }}
                disabled={busy}
              >
                {busy ? "Iniciando..." : "Executar backup"}
              </button>
            </>
          )}
          <button
            className="btn"
            onClick={() => {
              setShowBackups(true);
              reloadBackups();
            }}
          >
            {showBackups ? "Atualizar" : "Ver backups"}
          </button>
        </div>
        {showBackups && (
          bkLoading ? <Spinner /> :
          backups && backups.backups.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>UID</th><th>Tipo</th><th>Data</th><th>Itens</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {backups.backups.map((b) => (
                    <tr key={b.uid}>
                      <td className="mono cell-sub">{b.uid}</td>
                      <td>{titleCase(b.type)}</td>
                      <td className="cell-sub">{formatDate(b.timestamp)}</td>
                      <td>{b.results.length} item(ns)</td>
                      <td>
                        {b.results.some((r) => r.status === "error") ? (
                          <Badge value="error" />
                        ) : (
                          <Badge value="ok" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty>Nenhum backup registrado.</Empty>
        )}
      </Card>

      {/* ── Backup de BIOS (ROM via driver, somente leitura) ── */}
      <Card title="Backup de BIOS (ROM)">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          {can("actions.issue") && (
            <button
              className="btn primary"
              type="button"
              disabled={biosBusy || Boolean(biosDumps?.pending)}
              onClick={async () => {
                setBiosBusy(true);
                try {
                  await api(`/api/v3/devices/${uid}/requests`, {
                    method: "POST",
                    body: { type: "bios_backup", payload: { allowTestMode } },
                  });
                  setSuccess(
                    allowTestMode
                      ? "Backup de BIOS solicitado — o agente ativa o modo teste (consentido) e conclui apos o reinicio."
                      : "Backup de BIOS solicitado — sem alterar o Windows. Se o driver nao for aceito, o agente reporta a necessidade de modo teste.",
                  );
                  setShowBios(true);
                  setTimeout(() => reloadBios(), 2500);
                } catch {
                  setSuccess(null);
                } finally {
                  setBiosBusy(false);
                }
              }}
            >
              {biosBusy ? "Solicitando..." : "Solicitar dump de BIOS"}
            </button>
          )}
          <button
            className="btn"
            onClick={() => {
              setShowBios(true);
              reloadBios();
            }}
          >
            {showBios ? "Atualizar" : "Ver dumps"}
          </button>
          {can("actions.issue") && (
            <button
              className="btn"
              type="button"
              disabled={biosBusy}
              onClick={async () => {
                if (!window.confirm("Reverter modo de teste? Isto remove o driver BiosRead e desativa o testsigning (o watermark Test Mode some apos reiniciar).")) return;
                setBiosBusy(true);
                try {
                  await api(`/api/v3/devices/${uid}/requests`, {
                    method: "POST",
                    body: { type: "bios_revert", payload: {} },
                  });
                  setSuccess("Reversao solicitada — apos o agente executar, reinicie a estacao para remover o watermark.");
                } catch {
                  setSuccess(null);
                } finally {
                  setBiosBusy(false);
                }
              }}
            >
              Reverter modo teste
            </button>
          )}
        </div>
        {can("actions.issue") && (
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 12, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={allowTestMode}
              onChange={(e) => setAllowTestMode(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span style={{ fontSize: 13, color: "var(--muted, #9aa4b2)" }}>
              <strong style={{ color: "var(--text, #e6e9ef)" }}>Permitir modo de teste do Windows</strong> nesta estacao
              (testsigning) — <u>necessario</u> para carregar o driver nao assinado; mostra watermark &quot;Test Mode&quot;
              e exige reinicio. Use apenas em maquinas de teste.
            </span>
          </label>
        )}
        {biosDumps?.pending && (
          <div className="panel" style={{ marginBottom: 12 }}>
            <div className="panel-title">Dump em andamento</div>
            <div>
              <span className="mono">{biosDumps.pending.uid}</span> —{" "}
              {biosDumps.pending.status === "running"
                ? "o agente esta executando (na 1a. vez pode exigir reinicio para o testsigning; o fluxo sozinho apos o boot)"
                : "aguardando o proximo checkin do agente"}
            </div>
          </div>
        )}
        {biosDumps?.lastRequest && !biosDumps.pending && (
          <div className="panel" style={{ marginBottom: 12 }}>
            <div className="panel-title">Ultimo pedido de backup</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Badge
                value={
                  biosDumps.lastRequest.status === "failed" ||
                  (biosDumps.lastRequest.exitCode ?? 0) !== 0 ||
                  biosDumps.lastRequest.error
                    ? "error"
                    : "ok"
                }
                label={
                  biosDumps.lastRequest.status === "failed" ||
                  (biosDumps.lastRequest.exitCode ?? 0) !== 0 ||
                  biosDumps.lastRequest.error
                    ? "falhou"
                    : "ok"
                }
              />
              <span className="mono cell-sub">{biosDumps.lastRequest.uid}</span>
              <span className="cell-sub">{formatDate(biosDumps.lastRequest.finishedAt ?? biosDumps.lastRequest.createdAt)}</span>
            </div>
            <div style={{ marginTop: 6, fontSize: 13 }}>
              {biosDumps.lastRequest.output || biosDumps.lastRequest.error || "sem mensagem do agente"}
            </div>
          </div>
        )}
        {showBios && (
          biosLoading ? <Spinner /> :
          biosDumps && biosDumps.dumps.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Arquivo</th><th>Tamanho</th><th>FVs</th><th>Gaps</th><th>SHA256</th><th>Enviado</th><th></th></tr>
                </thead>
                <tbody>
                  {biosDumps.dumps.map((d) => (
                    <tr key={d.uid}>
                      <td className="mono">{d.filename}</td>
                      <td className="cell-sub">{(d.bytes / 1048576).toFixed(1)} MB</td>
                      <td>{d.fvh}</td>
                      <td>{d.gaps > 0 ? <Badge value="warning" label={`${d.gaps}`} /> : <Badge value="ok" />}</td>
                      <td className="mono cell-sub">{d.sha256.slice(0, 12)}…</td>
                      <td className="cell-sub">{formatDate(d.uploadedAt)}</td>
                      <td>
                        <button className="btn" type="button" onClick={() => downloadBiosDump(d)}>
                          Baixar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty>Nenhum dump de BIOS enviado.</Empty>
        )}
      </Card>

      <div className="grid cols-2">
        <Card title="Eventos" tight>
          {data.events.length === 0 ? (
            <Empty>Nenhum evento registrado.</Empty>
          ) : (
            <div style={{ padding: "0 16px" }}>
              <div className="timeline">
                {data.events.map((e) => (
                  <div className="timeline-item" key={e.id}>
                    <span className="when">{formatDate(e.at)}</span>
                    <span>
                      <div className="cell-sub mono">{e.type}</div>
                      <div>{e.message}</div>
                      <Badge value={e.severity} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card title="Requisicoes deste dispositivo" tight>
          {data.requests.length === 0 ? (
            <Empty>Nenhuma requisicao enviada.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th>Solicitante</th>
                    <th>Criada</th>
                  </tr>
                </thead>
                <tbody>
                  {data.requests.map((r) => (
                    <tr key={r.uid}>
                      <td>
                        <div className="cell-title">{titleCase(r.type)}</div>
                        <div className="cell-sub mono">{r.uid}</div>
                      </td>
                      <td>
                        <Badge value={r.status} />
                        {r.error ? <div className="cell-sub">{r.error}</div> : null}
                      </td>
                      <td className="cell-sub">{r.requestedBy}</td>
                      <td className="cell-sub">{relativeTime(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {active ? (
        <Modal
          title={`${active.label} — ${device.name}`}
          onClose={() => setActive(null)}
          footer={
            <>
              <button className="btn" onClick={() => setActive(null)} disabled={busy}>
                Cancelar
              </button>
              <button
                className={`btn ${active.danger ? "danger" : "primary"}`}
                onClick={submit}
                disabled={busy}
              >
                {busy ? "Enviando..." : "Confirmar acao"}
              </button>
            </>
          }
        >
          <p className="subtle">{active.description}</p>
          {formError ? <ErrorAlert message={formError} /> : null}

          {active.type === "freeze" ? (
            <>
              <div className="field">
                <label htmlFor="freeze-hours">Duracao (horas)</label>
                <select id="freeze-hours" className="select" value={freezeHours} onChange={(e) => setFreezeHours(e.target.value)}>
                  <option value="1">1 hora</option>
                  <option value="4">4 horas</option>
                  <option value="24">24 horas</option>
                  <option value="72">72 horas</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="freeze-msg">Mensagem exibida</label>
                <input id="freeze-msg" className="input" value={freezeMessage} onChange={(e) => setFreezeMessage(e.target.value)} />
              </div>
            </>
          ) : null}

          {active.type === "wipe" ? (
            <>
              <div className="field">
                <label htmlFor="wipe-scope">Escopo</label>
                <select id="wipe-scope" className="select" value={wipeScope} onChange={(e) => setWipeScope(e.target.value)}>
                  <option value="os">Somente sistema operacional</option>
                  <option value="full-disk">Disco completo</option>
                </select>
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" checked={wipeConfirm} onChange={(e) => setWipeConfirm(e.target.checked)} />
                Confirmo que esta acao e irreversivel
              </label>
            </>
          ) : null}

          {active.type === "file-delete" ? (
            <div className="field">
              <label htmlFor="file-path">Caminho do arquivo</label>
              <input id="file-path" className="input mono" value={filePath} onChange={(e) => setFilePath(e.target.value)} />
            </div>
          ) : null}

          {active.type === "message" ? (
            <div className="field">
              <label htmlFor="msg-body">Corpo da mensagem</label>
              <textarea id="msg-body" className="input" rows={3} value={messageBody} onChange={(e) => setMessageBody(e.target.value)} />
            </div>
          ) : null}

          {active.type === "script" ? (
            <div className="field">
              <label htmlFor="script-name">Script da biblioteca</label>
              <select id="script-name" className="select" value={scriptName} onChange={(e) => setScriptName(e.target.value)}>
                <option value="coletar-inventario">coletar-inventario</option>
                <option value="renovar-certificado">renovar-certificado</option>
                <option value="verificar-agent">verificar-agent</option>
              </select>
            </div>
          ) : null}

          {active.type === "block-program" ? (
            <>
              <div className="field">
                <label htmlFor="blk-pattern">Nome do programa</label>
                <input id="blk-pattern" className="input mono" placeholder="ex: notepad.exe" value={blockPattern} onChange={(e) => setBlockPattern(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="blk-action">Acao</label>
                <select id="blk-action" className="select" value={blockAction} onChange={(e) => setBlockAction(e.target.value as "block" | "unblock")}>
                  <option value="block">Bloquear</option>
                  <option value="unblock">Desbloquear</option>
                </select>
              </div>
            </>
          ) : null}

          {active.type === "backup" ? (
            <div className="field">
              <label htmlFor="bk-type">Tipo de backup</label>
              <select id="bk-type" className="select" value={backupType} onChange={(e) => setBackupType(e.target.value)}>
                <option value="quick">Rapido (docs + registro)</option>
                <option value="full">Completo (perfil de usuario)</option>
              </select>
            </div>
          ) : null}

          {active.type === "inventory-programs" || active.type === "inventory-updates" ||
           active.type === "install-updates" || active.type === "inventory-processes" ||
           active.type === "get-blocklist" ? (
            <div className="alert info">
              Esta acao coleta dados no endpoint. O resultado ficara disponivel nas secoes abaixo apos o agente reportar.
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
